
import React from "react";
import { Pencil } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Brand } from "@/hooks/useBrandManager";

interface BrandItemProps {
  brand: Brand;
  isSelected: boolean;
  onSelect: (brand: Brand) => void;
}

export const BrandItem = ({ 
  brand, 
  isSelected, 
  onSelect
}: BrandItemProps) => {
  const navigate = useNavigate();

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate('/brand-setup');
  };

  const normalizedWebsite = brand.website 
    ? brand.website.replace(/^https?:\/\//i, '').replace(/\/+$/, '')
    : null;

  return (
    <div 
      onClick={() => onSelect(brand)}
      className="flex items-center justify-between cursor-pointer p-2 w-full group hover:bg-muted/50 rounded-md transition-colors duration-200"
    >
      <div className="flex items-center flex-1 min-w-0 gap-2">
        {brand.favicon_url ? (
          <div className={`w-5 h-5 flex items-center justify-center rounded ${isSelected ? 'ring-2 ring-primary' : ''}`}>
            <img 
              src={brand.favicon_url} 
              alt={`${brand.name} favicon`}
              className="w-full h-full object-contain rounded"
            />
          </div>
        ) : (
          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isSelected ? 'bg-primary' : 'bg-muted'}`}></div>
        )}
        <div className="flex flex-col min-w-0 flex-1">
          <span className={`truncate text-sm font-medium ${isSelected ? 'text-primary' : 'text-foreground'}`}>
            {brand.name}
          </span>
          {normalizedWebsite && (
            <span className="truncate text-xs text-muted-foreground">
              {normalizedWebsite}
            </span>
          )}
        </div>
      </div>
      
      <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground hover:bg-muted rounded-full transition-colors duration-200"
          onClick={handleEditClick}
        >
          <Pencil className="h-3 w-3" />
          <span className="sr-only">Edit</span>
        </Button>
      </div>
    </div>
  );
};
